import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { adminMonitoringApi } from '../../lib/adminApi';
import { useAuth } from '../../contexts/AuthContext';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { AIServiceStatus } from '../../components/admin/AIServiceStatus';
import { AIGenerationToggle } from '../../components/admin/AIGenerationToggle';
import { ToastContainer, ToastType } from '../../components/Toast';
import { AdminDashboardSkeleton } from '../../components/skeletons/AdminDashboardSkeleton';
import { Users, AlertCircle, CheckCircle, Activity, Clock } from 'lucide-react';

interface Alert {
  id: string;
  type: 'error' | 'warning';
  title: string;
  message: string;
  actionPath?: string;
}

interface Toast {
  id: string;
  type: ToastType;
  title: string;
  message?: string;
  duration?: number;
}

export function AdminDashboard() {
  const navigate = useNavigate();
  const { authUser } = useAuth();
  const [totalUsers, setTotalUsers] = useState(0);
  const [proUsers, setProUsers] = useState(0);
  const [cronjobStatus, setCronjobStatus] = useState({
    unresolvedAlerts: 0,
    avgHealthScore: 100,
  });
  const [scheduledCampaignsCount, setScheduledCampaignsCount] = useState(0);
  const [lateCampaignsCount, setLateCampaignsCount] = useState(0);
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authUser) {
      loadAllData();
      const interval = setInterval(() => {
        loadCronjobStatus();
        loadScheduledCampaignsCount();
        loadCampaignAlerts();
      }, 30000);
      return () => clearInterval(interval);
    }
  }, [authUser]);

  const loadAllData = async () => {
    setLoading(true);
    await Promise.all([
      loadUserCount(),
      loadCronjobStatus(),
      loadScheduledCampaignsCount(),
      loadCampaignAlerts(),
    ]);
    setLoading(false);
  };

  const loadUserCount = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const result = await adminMonitoringApi.dashboardStats();
      setTotalUsers(result.total_users || 0);
      setProUsers(result.pro_users || 0);
    } catch (error) {
      console.error('Error loading user count:', error);
    }
  };

  const loadCronjobStatus = async () => {
    try {
      const [settingsRes, alertsRes] = await Promise.all([
        supabase.from('cronjob_settings').select('enabled, health_score'),
        supabase.from('cronjob_alerts').select('id, severity, message').eq('resolved', false).order('created_at', { ascending: false }),
      ]);

      const settings = settingsRes.data || [];
      const avgHealth = settings.length > 0
        ? settings.reduce((sum, s) => sum + (s.health_score || 100), 0) / settings.length
        : 100;

      const unresolvedCount = alertsRes.data?.length || 0;

      setCronjobStatus({
        unresolvedAlerts: unresolvedCount,
        avgHealthScore: Math.round(avgHealth),
      });

      updateAlerts(unresolvedCount, Math.round(avgHealth), alertsRes.data || [], lateCampaignsCount);
    } catch (error) {
      console.error('Failed to load cronjob status:', error);
    }
  };

  const loadScheduledCampaignsCount = async () => {
    try {
      const { count } = await supabase
        .from('email_campaigns')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'scheduled');

      setScheduledCampaignsCount(count || 0);
    } catch (error) {
      console.error('Failed to load scheduled campaigns count:', error);
    }
  };

  const loadCampaignAlerts = async () => {
    try {
      const { data: alertSummary, error } = await supabase
        .rpc('get_campaign_alerts_summary');

      if (error) {
        console.error('Failed to load campaign alerts:', error);
        return;
      }

      const lateStarted = alertSummary?.find((a: { alert_type: string; alert_count: number }) => a.alert_type === 'late_started');
      const lateCount = lateStarted?.alert_count || 0;

      setLateCampaignsCount(lateCount);
      updateAlerts(cronjobStatus.unresolvedAlerts, cronjobStatus.avgHealthScore, [], lateCount);
    } catch (error) {
      console.error('Failed to load campaign alerts:', error);
    }
  };

  const showToast = (type: ToastType, title: string, message?: string) => {
    const newToast: Toast = {
      id: Date.now().toString(),
      type,
      title,
      message,
      duration: 5000,
    };
    setToasts(prev => [...prev, newToast]);
  };

  const removeToast = (id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  const updateAlerts = (unresolvedAlerts: number, healthScore: number, cronjobAlerts: any[], lateCampaigns: number) => {
    const newAlerts: Alert[] = [];

    if (lateCampaigns > 0) {
      newAlerts.push({
        id: 'late-campaigns',
        type: 'warning',
        title: `${lateCampaigns} Campaign${lateCampaigns > 1 ? 's' : ''} Te Laat`,
        message: `Er ${lateCampaigns === 1 ? 'is' : 'zijn'} ${lateCampaigns} ingeplande campaign${lateCampaigns > 1 ? 's' : ''} die al verzonden had${lateCampaigns === 1 ? '' : 'den'} moeten zijn.`,
        actionPath: '/admin/campaigns',
      });
    }

    if (unresolvedAlerts > 0) {
      const latestAlert = cronjobAlerts[0];
      newAlerts.push({
        id: 'cronjob-alerts',
        type: 'error',
        title: `${unresolvedAlerts} Cronjob Alert${unresolvedAlerts > 1 ? 's' : ''}`,
        message: latestAlert?.message || 'Er zijn problemen met de cronjobs die aandacht vereisen.',
        actionPath: '/admin/cronjob-monitor',
      });
    } else if (healthScore < 80) {
      newAlerts.push({
        id: 'cronjob-health',
        type: 'warning',
        title: 'Cronjob Health Score Laag',
        message: `De gemiddelde health score is ${healthScore}%. Controleer de cronjob status.`,
        actionPath: '/admin/cronjob-monitor',
      });
    }

    setAlerts(newAlerts);
  };

  if (loading) {
    return (
      <AdminLayout>
        <AdminDashboardSkeleton />
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <ToastContainer toasts={toasts} onClose={removeToast} />

      <div className="max-w-5xl">
        <div className="mb-8">
          <h1 className="text-3xl font-semibold text-gray-900 mb-2">Admin Dashboard</h1>
          <p className="text-gray-600">Systeemoverzicht en status monitoring</p>
        </div>

        <AIGenerationToggle onShowToast={showToast} />

        {alerts.map(alert => (
          <div
            key={alert.id}
            className={`mb-6 p-4 rounded-xl border-2 ${
              alert.type === 'error'
                ? 'bg-red-50 border-red-200'
                : 'bg-yellow-50 border-yellow-200'
            }`}
          >
            <div className="flex items-start gap-3">
              <AlertCircle className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
                alert.type === 'error' ? 'text-red-600' : 'text-yellow-600'
              }`} />
              <div className="flex-1">
                <h3 className={`font-semibold mb-1 ${
                  alert.type === 'error' ? 'text-red-900' : 'text-yellow-900'
                }`}>
                  {alert.title}
                </h3>
                <p className={`text-sm ${
                  alert.type === 'error' ? 'text-red-700' : 'text-yellow-700'
                }`}>
                  {alert.message}
                </p>
                {alert.actionPath && (
                  <button
                    onClick={() => navigate(alert.actionPath!)}
                    className={`mt-3 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      alert.type === 'error'
                        ? 'bg-red-600 hover:bg-red-700 text-white'
                        : 'bg-yellow-600 hover:bg-yellow-700 text-white'
                    }`}
                  >
                    Bekijk Details
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <AIServiceStatus />
          <button
            onClick={() => navigate('/admin/users')}
            className="p-6 bg-white border border-gray-200 rounded-xl hover:border-gray-300 hover:shadow-sm transition-all text-left"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-gray-100 rounded-lg">
                <Users className="w-5 h-5 text-gray-700" />
              </div>
              <span className="text-sm text-gray-600 font-medium">Gebruikers</span>
            </div>
            <div className="text-3xl font-semibold text-gray-900">{totalUsers}</div>
            {proUsers > 0 && (
              <div className="mt-2 text-sm text-gray-600 font-medium">
                {proUsers} PRO
              </div>
            )}
          </button>

          <button
            onClick={() => navigate('/admin/cronjob-monitor')}
            className="p-6 bg-white border border-gray-200 rounded-xl hover:border-gray-300 hover:shadow-sm transition-all text-left"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className={`p-2 rounded-lg ${
                cronjobStatus.unresolvedAlerts > 0
                  ? 'bg-red-100'
                  : cronjobStatus.avgHealthScore < 80
                  ? 'bg-yellow-100'
                  : 'bg-green-100'
              }`}>
                {cronjobStatus.unresolvedAlerts > 0 ? (
                  <AlertCircle className="w-5 h-5 text-red-600" />
                ) : cronjobStatus.avgHealthScore < 80 ? (
                  <Activity className="w-5 h-5 text-yellow-600" />
                ) : (
                  <CheckCircle className="w-5 h-5 text-green-600" />
                )}
              </div>
              <span className="text-sm text-gray-600 font-medium">Cronjob Health</span>
            </div>
            <div className="text-3xl font-semibold text-gray-900">{cronjobStatus.avgHealthScore}%</div>
            {cronjobStatus.unresolvedAlerts > 0 && (
              <div className="mt-2 text-sm text-red-600 font-medium">
                {cronjobStatus.unresolvedAlerts} alert{cronjobStatus.unresolvedAlerts > 1 ? 's' : ''}
              </div>
            )}
          </button>

          <button
            onClick={() => navigate('/admin/campaigns')}
            className="p-6 bg-white border border-gray-200 rounded-xl hover:border-gray-300 hover:shadow-sm transition-all text-left"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className={`p-2 rounded-lg ${
                lateCampaignsCount > 0 ? 'bg-yellow-100' : 'bg-gray-100'
              }`}>
                <Clock className={`w-5 h-5 ${
                  lateCampaignsCount > 0 ? 'text-yellow-600' : 'text-gray-700'
                }`} />
              </div>
              <span className="text-sm text-gray-600 font-medium">Geplande Emails</span>
            </div>
            <div className="text-3xl font-semibold text-gray-900">{scheduledCampaignsCount}</div>
            {lateCampaignsCount > 0 && (
              <div className="mt-2 text-sm text-yellow-600 font-medium">
                {lateCampaignsCount} te laat
              </div>
            )}
          </button>
        </div>
      </div>
    </AdminLayout>
  );
}
